/**
 * CR-SEQ-2026-025 — builder state for the ad-hoc General Report.
 *
 * Everything the operator has chosen lives here as catalogue names; labels
 * and types are always resolved against the fetched catalogue, never stored.
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  CatalogField,
  CatalogLevel,
  ReportFilter,
} from "@services/reportEngine";
import { useReportCatalog } from "@hooks/query/useReportCatalog";

type AdhocReportState = {
  /** Set when the builder was opened from a saved template. */
  templateId: string | null;
  templateName: string | null;
  selectedLevels: string[];
  toggleLevel: (name: string) => void;
  selectedFields: string[];
  setSelectedFields: (names: string[]) => void;
  toggleField: (name: string) => void;
  filters: ReportFilter[];
  setFilters: (filters: ReportFilter[]) => void;
  accountingPeriodId: string | undefined;
  setAccountingPeriodId: (id: string | undefined) => void;
  fromDate: string;
  toDate: string;
  setFromDate: (value: string) => void;
  setToDate: (value: string) => void;
  announcedAllocations: "as_at" | "in_range";
  setAnnouncedAllocations: (mode: "as_at" | "in_range") => void;
  currentStep: number;
  goToStep: (step: number) => void;
  isEditingColumns: boolean;
  editColumns: () => void;
  // Derived from the catalogue
  catalog: CatalogLevel[];
  isCatalogLoading: boolean;
  isCatalogError: boolean;
  refetchCatalog: () => void;
  finestLevel: CatalogLevel | null;
  availableFields: CatalogField[];
  fieldByName: Map<string, CatalogField>;
  loadTemplate: (
    template: {
      id: string;
      name: string;
      config: {
        dataLevels: string[];
        fields: string[];
        filters: ReportFilter[];
      };
    },
    mode: "run" | "edit",
  ) => void;
  /** Remember a template the operator just saved, so later steps can name it. */
  rememberSavedTemplate: (template: { id: string; name: string }) => void;
  /**
   * A template run is read-only apart from the dates, as in DEQ: filters show
   * as chips and the columns cannot be changed. Editing goes through the
   * builder instead (the Edit action on the template list).
   */
  isTemplateRun: boolean;
};

const AdhocReportContext = createContext<AdhocReportState>(
  {} as AdhocReportState,
);

export const useAdhocReport = () => useContext(AdhocReportContext);

export const AdhocReportProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: catalog = [],
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useReportCatalog();

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<ReportFilter[]>([]);
  const [accountingPeriodId, setAccountingPeriodId] = useState<
    string | undefined
  >();
  // All-scheme reports use DEQ's calendar-year prefill. When a scheme is
  // selected, the run step replaces this with that scheme's real accounting
  // period. Build the local calendar date without converting through UTC.
  const today = new Date();
  const [fromDate, setFromDate] = useState(`${today.getFullYear()}-01-01`);
  const [toDate, setToDate] = useState(
    [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, "0"),
      String(today.getDate()).padStart(2, "0"),
    ].join("-"),
  );
  const [announcedAllocations, setAnnouncedAllocations] = useState<
    "as_at" | "in_range"
  >("in_range");
  const [currentStep, setCurrentStep] = useState(0);
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [isTemplateRun, setIsTemplateRun] = useState(false);

  const fieldByName = useMemo(() => {
    const map = new Map<string, CatalogField>();
    for (const level of catalog) {
      for (const field of level.fields) map.set(field.name, field);
    }
    return map;
  }, [catalog]);

  // Subjects are independent choices. The most detailed selected subject
  // determines what one result row represents; selecting a measure-only
  // subject such as Usage does not make the row grain change unexpectedly.
  const finestLevel = useMemo(() => {
    const chosen = catalog.filter(level => selectedLevels.includes(level.name));
    if (chosen.length === 0) return null;
    return chosen.reduce((finest, level) =>
      level.grainRank > finest.grainRank ? level : finest,
    );
  }, [catalog, selectedLevels]);

  const availableFields = useMemo(() => {
    if (!finestLevel) return [];
    const rowLevelName =
      catalog.find(level => level.grain === finestLevel.grain)?.name ??
      finestLevel.name;

    return catalog
      .filter(level => selectedLevels.includes(level.name))
      .flatMap(level => level.fields)
      .filter(field => field.availableAt.includes(rowLevelName));
  }, [catalog, finestLevel, selectedLevels]);

  const toggleLevel = useCallback(
    (name: string) => {
      setSelectedLevels(current => {
        const nextLevel = catalog.find(level => level.name === name);
        if (!nextLevel) return current;
        if (current.includes(name)) {
          return current.filter(level => level !== name);
        }

        // The UI disables cross-family choices until the current family is
        // unticked. Keep this guard so a programmatic toggle cannot silently
        // discard a configured report either.
        const hasDifferentFamily = current.some(
          levelName =>
            catalog.find(level => level.name === levelName)?.family !==
            nextLevel.family,
        );
        return hasDifferentFamily ? current : [...current, name];
      });
    },
    [catalog],
  );

  const toggleField = useCallback((name: string) => {
    setSelectedFields(current =>
      current.includes(name)
        ? current.filter(field => field !== name)
        : [...current, name],
    );
  }, []);

  const rememberSavedTemplate = useCallback(
    (template: { id: string; name: string }) => {
      setTemplateId(template.id);
      setTemplateName(template.name);
    },
    [],
  );

  const loadTemplate = useCallback(
    (
      template: {
        id: string;
        name: string;
        config: {
          dataLevels: string[];
          fields: string[];
          filters: ReportFilter[];
        };
      },
      mode: "run" | "edit",
    ) => {
      setTemplateId(template.id);
      setTemplateName(template.name);
      setSelectedLevels(template.config.dataLevels);
      setSelectedFields(template.config.fields);
      setFilters(template.config.filters ?? []);
      setIsTemplateRun(mode === "run");
      setIsEditingColumns(false);
      // Running goes straight to the run step, as DEQ's run page does;
      // editing starts at the columns, as DEQ's edit does.
      setCurrentStep(mode === "run" ? 2 : 0);
    },
    [],
  );

  const value: AdhocReportState = {
    templateId,
    templateName,
    selectedLevels,
    toggleLevel,
    selectedFields,
    setSelectedFields,
    toggleField,
    filters,
    setFilters,
    accountingPeriodId,
    setAccountingPeriodId,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    announcedAllocations,
    setAnnouncedAllocations,
    currentStep,
    goToStep: step => {
      setIsEditingColumns(false);
      setCurrentStep(step);
    },
    isEditingColumns,
    editColumns: () => {
      setIsEditingColumns(true);
      setCurrentStep(0);
    },
    catalog,
    isCatalogLoading,
    isCatalogError,
    refetchCatalog,
    finestLevel,
    availableFields,
    fieldByName,
    loadTemplate,
    rememberSavedTemplate,
    isTemplateRun,
  };

  return (
    <AdhocReportContext.Provider value={value}>
      {children}
    </AdhocReportContext.Provider>
  );
};
